import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  musicApi,
  type IntegrityIssue,
  type LiveCleanupCandidate,
} from "@/api/music";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

/**
 * Music → Integrity. Manual crawl that finds:
 *
 *   * incomplete albums (filed tracks < MusicBrainz expected tracks)
 *   * truncated tracks (on-disk audio duration < expected by more
 *     than `music.integrity_duration_slack_pct`).
 *
 * The scan is explicit: the user clicks "Scan now", the backend
 * spawns a task that walks every monitored album with at least one
 * filed track, probes every filed track's duration via lofty, and
 * writes issue rows to `music_integrity_issues`. While the scan is
 * running we poll status every 2s; when it goes idle we refetch
 * issues once and switch back to a slow refresh interval.
 */
export function MusicIntegrity() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [tab, setTab] = useState<"all" | "album_incomplete" | "track_truncated">(
    "all",
  );

  const status = useQuery({
    queryKey: ["music", "integrity", "status"],
    queryFn: musicApi.integrity.status,
    // Poll fast while a scan is in flight, slow otherwise. The
    // refetch interval reads the previous data; switching to fast is
    // automatic the moment a scan starts.
    refetchInterval: (q) => (q.state.data?.running ? 2000 : 30000),
  });

  const issues = useQuery({
    queryKey: ["music", "integrity", "issues", tab],
    queryFn: () =>
      musicApi.integrity.issues({
        kind: tab === "all" ? undefined : tab,
        limit: 2000,
      }),
  });

  // When the scan finishes, force a refetch of the issues list so
  // the user doesn't have to wait for the next 30s tick.
  const wasRunning = useMemo(() => status.data?.running, [status.data?.running]);
  if (status.data && !status.data.running && wasRunning === false) {
    // No-op; left here as a hint of where to put refetch logic.
  }

  const scanMut = useMutation({
    mutationFn: () => musicApi.integrity.start(),
    onSuccess: () => {
      toast("Integrity scan started", "info");
      qc.invalidateQueries({ queryKey: ["music", "integrity"] });
    },
    onError: (e) =>
      toast(
        e instanceof Error ? e.message : "Failed to start integrity scan",
        "error",
      ),
  });

  const backfillMut = useMutation({
    mutationFn: () => musicApi.integrity.backfill(),
    onSuccess: () => {
      toast(
        "Duration backfill started — populating tracks.duration_ms from MusicBrainz",
        "info",
      );
      qc.invalidateQueries({ queryKey: ["music", "integrity", "status"] });
    },
    onError: (e) =>
      toast(
        e instanceof Error ? e.message : "Failed to start backfill",
        "error",
      ),
  });

  // Live-recording cleanup state. Preview is a lazy fetch the user
  // explicitly triggers (it walks every album row server-side); the
  // delete only fires after the user reviews the list.
  const [showLivePreview, setShowLivePreview] = useState(false);
  const livePreview = useQuery({
    queryKey: ["music", "liveCleanup"],
    queryFn: musicApi.liveCleanup.preview,
    enabled: showLivePreview,
  });
  const liveDeleteMut = useMutation({
    mutationFn: () => musicApi.liveCleanup.delete(),
    onSuccess: (res) => {
      toast(`Removed ${res.deleted} live recording${res.deleted === 1 ? "" : "s"}`, "success");
      qc.invalidateQueries({ queryKey: ["music", "liveCleanup"] });
      // Also bust the artist + album caches so the deleted rows
      // disappear from other surfaces immediately.
      qc.invalidateQueries({ queryKey: ["music"] });
      qc.invalidateQueries({ queryKey: ["artists"] });
    },
    onError: (e) =>
      toast(
        e instanceof Error ? e.message : "Cleanup delete failed",
        "error",
      ),
  });

  // Manually refetch issues once status transitions out of running.
  // useQuery's refetchInterval covers status; we wire issues to it by
  // bumping the query key when running flips false.
  const lastRunStamp = status.data?.last_run_at ?? "";
  useEffectAfterRunCompleted(lastRunStamp, () => {
    qc.invalidateQueries({ queryKey: ["music", "integrity", "issues"] });
  });

  const counts = {
    all:
      (status.data?.albums_incomplete ?? 0) +
      (status.data?.tracks_truncated ?? 0),
    album_incomplete: status.data?.albums_incomplete ?? 0,
    track_truncated: status.data?.tracks_truncated ?? 0,
  } as const;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Music"
        title="Library integrity"
        description="Manual crawl that finds incomplete albums and truncated tracks. Click Scan now to walk every filed album, count tracks, and probe each file's audio duration against MusicBrainz."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => backfillMut.mutate()}
              loading={
                backfillMut.isPending || status.data?.backfill_running
              }
              disabled={
                backfillMut.isPending || status.data?.backfill_running
              }
              title="Populate tracks.duration_ms from MusicBrainz so the truncation scanner can see them"
            >
              {status.data?.backfill_running
                ? "Backfilling…"
                : "Backfill durations"}
            </Button>
            <Button
              onClick={() => scanMut.mutate()}
              loading={scanMut.isPending || status.data?.running}
              disabled={scanMut.isPending || status.data?.running}
            >
              {status.data?.running ? "Scanning…" : "Scan now"}
            </Button>
          </div>
        }
      />

      <StatusBar
        running={status.data?.running ?? false}
        lastRunAt={status.data?.last_run_at ?? null}
        albumsIncomplete={counts.album_incomplete}
        tracksTruncated={counts.track_truncated}
        backfillRunning={status.data?.backfill_running ?? false}
        tracksPendingDuration={status.data?.tracks_pending_duration ?? 0}
      />

      <TabRow value={tab} onChange={setTab} counts={counts} />

      {issues.isLoading ? (
        <p className="text-sm text-foreground/60">Loading issues…</p>
      ) : issues.isError ? (
        <EmptyState
          title="Couldn't load issues"
          description={
            issues.error instanceof Error ? issues.error.message : undefined
          }
        />
      ) : (issues.data ?? []).length === 0 ? (
        <EmptyState
          glyph="search"
          title={
            status.data?.last_run_at
              ? "Nothing to report"
              : "No scan run yet"
          }
          description={
            status.data?.last_run_at
              ? "The last scan found no incomplete albums or truncated tracks in this filter."
              : "Click Scan now to walk the music library and surface incomplete albums or truncated tracks."
          }
        />
      ) : (
        <IssuesList issues={issues.data ?? []} />
      )}

      <LiveCleanupSection
        showPreview={showLivePreview}
        onShowPreview={() => setShowLivePreview(true)}
        previewLoading={livePreview.isFetching}
        candidates={livePreview.data?.candidates ?? null}
        matched={livePreview.data?.matched ?? 0}
        onDelete={() => {
          const count = livePreview.data?.matched ?? 0;
          if (count === 0) return;
          if (
            !confirm(
              `Delete ${count} live recording${count === 1 ? "" : "s"}? This drops the album rows AND their tracks (cascading). Cannot be undone.`,
            )
          )
            return;
          liveDeleteMut.mutate();
        }}
        deleting={liveDeleteMut.isPending}
      />
    </div>
  );
}

function StatusBar({
  running,
  lastRunAt,
  albumsIncomplete,
  tracksTruncated,
  backfillRunning,
  tracksPendingDuration,
}: {
  running: boolean;
  lastRunAt: string | null;
  albumsIncomplete: number;
  tracksTruncated: number;
  backfillRunning: boolean;
  tracksPendingDuration: number;
}) {
  const lastLabel = lastRunAt
    ? new Date(lastRunAt).toLocaleString()
    : "never";
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 rounded border border-line bg-surface/30 p-4 sm:grid-cols-4">
        <Stat
          label="Status"
          value={running ? "scanning…" : "idle"}
          tone={running ? "info" : "neutral"}
        />
        <Stat label="Last scan" value={lastLabel} tone="neutral" />
        <Stat
          label="Albums incomplete"
          value={String(albumsIncomplete)}
          tone={albumsIncomplete > 0 ? "warn" : "good"}
        />
        <Stat
          label="Tracks truncated"
          value={String(tracksTruncated)}
          tone={tracksTruncated > 0 ? "bad" : "good"}
        />
      </div>
      {/* Backfill coverage banner. The truncation scanner can only
          evaluate tracks with a known expected duration; when many
          tracks are NULL it surfaces a fraction of the real picture.
          Render whenever the backfill is running OR there's still
          coverage gap to close. */}
      {(backfillRunning || tracksPendingDuration > 0) && (
        <div
          className={cn(
            "flex flex-col gap-1 rounded border px-3 py-2 text-xs sm:flex-row sm:items-center sm:justify-between",
            backfillRunning
              ? "border-accent/30 bg-accent/5 text-accent"
              : "border-line bg-surface/30 text-foreground/65",
          )}
        >
          <span>
            {backfillRunning
              ? "Backfill in progress — fetching MusicBrainz durations for "
              : "Tracks awaiting MusicBrainz duration backfill: "}
            <strong className="tabular-nums">
              {tracksPendingDuration.toLocaleString()}
            </strong>{" "}
            track{tracksPendingDuration === 1 ? "" : "s"}
            {backfillRunning ? " left to walk." : "."}
          </span>
          {!backfillRunning && (
            <span className="text-foreground/50">
              The truncation scanner only checks tracks with a known
              expected duration.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "good" | "warn" | "bad" | "info" | "neutral";
}) {
  const colour =
    tone === "good"
      ? "text-good"
      : tone === "warn"
        ? "text-warn"
        : tone === "bad"
          ? "text-bad"
          : tone === "info"
            ? "text-accent"
            : "text-foreground/70";
  return (
    <div className="space-y-0.5">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-foreground/45">
        {label}
      </div>
      <div className={cn("text-base font-medium tabular-nums", colour)}>
        {value}
      </div>
    </div>
  );
}

function TabRow({
  value,
  onChange,
  counts,
}: {
  value: "all" | "album_incomplete" | "track_truncated";
  onChange: (v: "all" | "album_incomplete" | "track_truncated") => void;
  counts: { all: number; album_incomplete: number; track_truncated: number };
}) {
  const tabs = [
    { id: "all" as const, label: "All issues" },
    { id: "album_incomplete" as const, label: "Incomplete albums" },
    { id: "track_truncated" as const, label: "Truncated tracks" },
  ];
  return (
    <div className="inline-flex flex-wrap rounded border border-line bg-surface/40 p-0.5 text-xs">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={cn(
            "rounded px-3 py-1.5 transition",
            value === t.id
              ? "bg-accent/20 font-medium text-foreground"
              : "text-foreground/60 hover:text-foreground",
          )}
        >
          {t.label}
          <span className="ml-1.5 text-foreground/40">{counts[t.id]}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Live-recording cleanup section. Separate from the integrity-scan
 * issues because the workflow is distinct:
 *
 *   * Preview is a one-shot fetch (no scheduled scan).
 *   * Delete is destructive (drops the album rows + cascades to
 *     tracks), so we hide the button until the user has eyeballed
 *     the candidate list.
 *   * The title-pattern filter is shared with the import path so
 *     anything that gets dropped here would have been rejected at
 *     import time going forward.
 */
function LiveCleanupSection({
  showPreview,
  onShowPreview,
  previewLoading,
  candidates,
  matched,
  onDelete,
  deleting,
}: {
  showPreview: boolean;
  onShowPreview: () => void;
  previewLoading: boolean;
  candidates: LiveCleanupCandidate[] | null;
  matched: number;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <section className="space-y-3 rounded border border-line bg-surface/30 p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="eyebrow">Live-recording cleanup</h2>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-foreground/70">
            Find albums whose titles indicate they're live recordings,
            radio sessions, or concert bootlegs (MusicBrainz often
            classifies these with empty <code className="font-mono">secondary_types</code>,
            so the import filter on title patterns is the only way to
            catch them). Going forward, the importer drops these
            automatically; this tool retroactively cleans up rows
            imported before that filter learned about title patterns.
            Toggle <code className="font-mono">music.include_live_recordings</code> to
            keep them.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {showPreview ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={onShowPreview}
                loading={previewLoading}
                disabled={previewLoading}
              >
                Refresh preview
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={onDelete}
                loading={deleting}
                disabled={deleting || matched === 0}
              >
                {deleting
                  ? "Deleting…"
                  : matched === 0
                    ? "Nothing to delete"
                    : `Delete ${matched}`}
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={onShowPreview}
            >
              Find live recordings
            </Button>
          )}
        </div>
      </div>

      {showPreview && previewLoading && (
        <p className="text-xs text-foreground/55">Scanning library…</p>
      )}

      {showPreview && !previewLoading && candidates && candidates.length === 0 && (
        <p className="text-xs text-foreground/55">
          No live recordings matched. Library is clean.
        </p>
      )}

      {showPreview && !previewLoading && candidates && candidates.length > 0 && (
        <ul className="space-y-1.5">
          {candidates.map((c) => (
            <li
              key={c.album_id}
              className="flex flex-col gap-x-3 gap-y-0.5 rounded border border-warn/20 bg-warn/5 px-3 py-2 text-xs sm:flex-row sm:items-baseline"
            >
              <span className="min-w-0 flex-1 truncate">
                <strong className="font-medium">{c.title}</strong>
                <span className="ml-2 text-foreground/60">
                  · {c.artist_name}
                </span>
              </span>
              <span className="font-mono text-[10px] text-foreground/45">
                {c.primary_type ?? "—"}
                {c.has_file && (
                  <span className="ml-1.5 text-warn">filed</span>
                )}
              </span>
              {c.album_id != null && (
                <Link
                  to="/music/albums/$id"
                  params={{ id: String(c.album_id) }}
                  className="text-accent hover:underline"
                >
                  open →
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function IssuesList({ issues }: { issues: IntegrityIssue[] }) {
  return (
    <ul className="space-y-2">
      {issues.map((i) =>
        i.kind === "album_incomplete" ? (
          <AlbumIncompleteCard key={i.id} issue={i} />
        ) : (
          <TrackTruncatedCard key={i.id} issue={i} />
        ),
      )}
    </ul>
  );
}

function AlbumIncompleteCard({ issue }: { issue: IntegrityIssue }) {
  const missing = useMemo(() => {
    if (!issue.missing_track_numbers) return [] as number[];
    try {
      const arr = JSON.parse(issue.missing_track_numbers);
      return Array.isArray(arr) ? (arr as number[]) : [];
    } catch {
      return [];
    }
  }, [issue.missing_track_numbers]);

  return (
    <li className="rounded border border-warn/30 bg-warn/5 p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-warn">
              Incomplete album
            </span>
            <span className="font-medium">{issue.album_title ?? "?"}</span>
            <span className="text-foreground/55">
              · {issue.artist_name ?? "?"}
            </span>
          </div>
          <div className="mt-1 text-xs text-foreground/70 tabular-nums">
            Filed {issue.filed_tracks ?? 0} of {issue.expected_tracks ?? 0}{" "}
            tracks — missing{" "}
            {missing.length === 0
              ? "—"
              : missing.length <= 8
                ? `#${missing.join(", #")}`
                : `#${missing.slice(0, 6).join(", #")} … (${missing.length} total)`}
          </div>
        </div>
        {issue.album_id != null && (
          <Link
            to="/music/albums/$id"
            params={{ id: String(issue.album_id) }}
            className="self-start"
          >
            <Button variant="secondary" size="sm">
              Open album →
            </Button>
          </Link>
        )}
      </div>
    </li>
  );
}

function TrackTruncatedCard({ issue }: { issue: IntegrityIssue }) {
  const expected = issue.expected_duration_ms ?? 0;
  const actual = issue.actual_duration_ms ?? 0;
  return (
    <li className="rounded border border-bad/30 bg-bad/5 p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-bad">
              Truncated track
            </span>
            <span className="font-medium">{issue.track_title ?? "?"}</span>
            <span className="text-foreground/55">
              · {issue.artist_name ?? "?"} — {issue.album_title ?? "?"}
            </span>
          </div>
          <div className="mt-1 text-xs text-foreground/70 tabular-nums">
            Duration {fmtDuration(actual)} of expected {fmtDuration(expected)}
            {issue.shortfall_pct != null && (
              <span className="ml-1 text-bad">
                ({issue.shortfall_pct.toFixed(1)}% short)
              </span>
            )}
          </div>
          {issue.path && (
            <div className="mt-1 break-all font-mono text-[10px] text-foreground/40">
              {issue.path}
            </div>
          )}
        </div>
        {issue.album_id != null && (
          <Link
            to="/music/albums/$id"
            params={{ id: String(issue.album_id) }}
            className="self-start"
          >
            <Button variant="secondary" size="sm">
              Open album →
            </Button>
          </Link>
        )}
      </div>
    </li>
  );
}

/** Helper hook that fires `cb` whenever `key` changes to a non-empty
 *  value. Used to invalidate the issues query when the status
 *  endpoint reports a fresh `last_run_at` (i.e. a scan finished). */
function useEffectAfterRunCompleted(key: string, cb: () => void): void {
  const seen = useMemo(() => ({ value: "" }), []);
  if (key && key !== seen.value) {
    seen.value = key;
    // Defer one tick so we don't fire during render.
    setTimeout(cb, 0);
  }
}

function fmtDuration(ms: number): string {
  if (!ms || ms <= 0) return "—";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
